import React, { useMemo, useState } from "react";
import { Search, Download, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { exportCSV } from "@/lib/format";

export function DataTable({
  columns, data, searchKeys = [], exportName = "export", exportColumns,
  toolbar, emptyText = "Tidak ada data", testId = "data-table",
}) {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const filtered = useMemo(() => {
    let rows = data || [];
    if (q.trim()) {
      const t = q.toLowerCase();
      rows = rows.filter((r) =>
        searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(t))
      );
    }
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      rows = [...rows].sort((a, b) => {
        let av = col?.sortVal ? col.sortVal(a) : a[sortKey];
        let bv = col?.sortVal ? col.sortVal(b) : b[sortKey];
        if (typeof av === "string") av = av.toLowerCase();
        if (typeof bv === "string") bv = bv.toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [data, q, sortKey, sortDir, columns, searchKeys]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-b border-slate-200">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          {searchKeys.length > 0 && (
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                data-testid={`${testId}-search`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari..."
                className="pl-8 h-9 text-sm"
              />
            </div>
          )}
          <span className="text-xs text-slate-500 whitespace-nowrap">{filtered.length} baris</span>
        </div>
        <div className="flex items-center gap-2">
          {toolbar}
          <Button
            data-testid={`${testId}-export`}
            variant="outline" size="sm"
            onClick={() => exportCSV(filtered, exportColumns || columns.filter((c) => !c.noExport), exportName)}
            className="h-9 gap-1.5"
          >
            <Download className="w-4 h-4" /> Export Excel
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
        <table className="w-full text-left border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-100">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 border-b border-slate-200 whitespace-nowrap"
                >
                  {c.sortable !== false ? (
                    <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleSort(c.key)}>
                      {c.label}
                      {sortKey === c.key ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-slate-400">{emptyText}</td></tr>
            ) : filtered.map((row, i) => (
              <tr key={row.id || i} className="odd:bg-white even:bg-slate-50/50 hover:bg-sky-50/60 transition-colors">
                {columns.map((c) => (
                  <td key={c.key} className={`px-3 py-2.5 text-sm text-slate-800 border-b border-slate-100 ${c.mono ? "font-mono tabular-nums" : ""} ${c.className || ""}`}>
                    {c.render ? c.render(row) : String(row[c.key] ?? "-")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
